import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { ResumeIndicatorM2ByHealthTeamData } from "@/services/esus/indicator/emulti/m2/getResumeIndicatorM2ByHealthTeam";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEmultiEnum,
} from "@/utils/indicatorsEmulti";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEmultiM2HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalAttendances: Column;
	totalSharedAttendances: Column;
	totalNonSharedAttendances: Column;
	classificationAverage: Column;
	classification: Column;
};

type IndicatorEmultiM2HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorM2ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEmultiEnum.M2);

export function IndicatorEmultiM2HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEmultiM2HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEmultiM2HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalAttendances: { title: "Total de ações", visible: true },
		totalSharedAttendances: { title: "Ações compartilhadas", visible: true },
		totalNonSharedAttendances: { title: "Ações não compartilhadas", visible: true },
		classificationAverage: { title: "Média de ações compartilhadas", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>
					<th className={`px-2 ${!columns.totalAttendances.visible && "d-none"}`}>
						{columns.totalAttendances.title}{" "}
						<CardOverlayTrigger header="Total de ações" icon={<FaInfoCircle />}>
							<div className="text-start">
								Atendimentos individuais, atendimentos odontológicos e atividades
								coletivas, <br />
								compartilhadas ou não, bem como cuidados compartilhados, realizados
								no <br />
								período em que pelo menos um dos profissionais pertença a uma{" "}
								<br />
								equipe emulti.
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.totalSharedAttendances.visible && "d-none"}`}>
						{columns.totalSharedAttendances.title}{" "}
						<CardOverlayTrigger header="Ações compartilhadas" icon={<FaInfoCircle />}>
							<div className="text-start">
								Atendimentos individuais, atendimentos odontológicos e atividades
								coletivas compartilhadas, bem como cuidados compartilhados, <br />
								realizados no período onde pelo menos 1 dos profissionais
								pertença a uma equipe emulti. <br />
								Em casos de atendimentos individuais, pelo menos 1 dos demais
								<br />
								profissionais devem pertencer a uma das seguintes equipes:
								<br />
								eSF - equipe da equipe Saúde da Família <br />
								eSFR - equipe Saúde da Família Ribeirinha <br />
								eCR - equipe Consultório na Rua <br />
								eAP - equipe de Atenção Primária <br />
								UBSF - equipe de Unidade Básica de Saúde Fluvial <br />
								OBS: Atendimentos individuais compartilhados entre profissionais{" "}
								<br />
								da mesma equipe emulti também serão considerados desde que sejam{" "}
								<br />
								registrados por 2 profissionais diferentes (CNS distintos).
							</div>
						</CardOverlayTrigger>
					</th>
					<th
						className={`px-2 ${!columns.totalNonSharedAttendances.visible && "d-none"}`}
					>
						{columns.totalNonSharedAttendances.title}{" "}
						<CardOverlayTrigger
							header="Ações não compartilhadas"
							icon={<FaInfoCircle />}
						>
							<div className="text-start">
								Quantidade de Atendimentos individuais, atendimentos odontológicos e
								atividades coletivas <br />
								realizadas exclusivamente por 1 profissional de uma equipe emulti no
								período.
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationAverage.visible && "d-none"}`}>
						{columns.classificationAverage.title}{" "}
						<CardOverlayTrigger
							header="Média de ações compartilhadas"
							message="Percentual de ações compartilhadas em relação às não compartilhadas realizadas pela emulti no período"
							icon={<FaInfoCircle />}
						/>
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						{columns.classification.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: > ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-success">{`Bom: > ${indicatorClassificationScores.good}% e <= ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: > ${indicatorClassificationScores.enough}% e <= ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: <= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEmultiM2HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEmultiM2HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEmultiM2HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.healthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.healthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalAttendances.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalAttendances ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalSharedAttendances.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalSharedAttendances ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalNonSharedAttendances.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalNonSharedAttendances ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationAverage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalSharedAttendances,
										healthTeamClassification.totalAttendances
									)}`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEmultiEnum.M2,
											calculatePercentage(
												healthTeamClassification.totalSharedAttendances,
												healthTeamClassification.totalAttendances
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEmultiEnum.M2,
											calculatePercentage(
												healthTeamClassification.totalSharedAttendances,
												healthTeamClassification.totalAttendances
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
